/**
 * Таблица. Одна на шесть экранов макета: архив задач, пользователи, роли,
 * проекты, приоритеты, типы задач, таблица задач в отчётности.
 *
 * Все состояния встроены — загрузка, пусто, ошибка. Компонент, у которого
 * их нет, вынуждает каждый экран изобретать свои, и они расходятся.
 *
 * Сортировка контролируемая: она уходит в URL (§16.4), а не живёт внутри.
 *
 * На телефоне (docs/08-mobile-roadmap.md, M2) строки — карточками: таблица
 * в шесть столбцов на 360 px читается только прокруткой вбок. Заголовок
 * карточки — столбцы с mobile: "title" (по умолчанию первый), остальные —
 * парами «подпись — значение»; сортировка — выбором над списком.
 */

import { ArrowDown, ArrowUp } from "lucide-react";
import type { ReactNode } from "react";

import { useIsPhone } from "@/shared/lib/media";

import { cn } from "./cn";
import { IconButton } from "./IconButton";
import { Select } from "./Select";
import { EmptyState, ErrorState, SkeletonRows } from "./states";
import { ArrowUpDown } from "./motionIcons";

export type SortDirection = "asc" | "desc";

export interface SortState {
  field: string;
  direction: SortDirection;
}

export interface Column<T> {
  key: string;
  header: string;
  render: (row: T) => ReactNode;
  sortable?: boolean;
  /** Ширина столбца; без неё колонки прыгают между страницами. */
  width?: string;
  align?: "left" | "right";
  /** Числа выравниваются по разряду — иначе колонку невозможно читать. */
  numeric?: boolean;
  /** Роль в карточке на телефоне: заголовок, поле (по умолчанию) или скрыт.
   *  Если заголовок не отмечен ни у одного столбца, им становится первый. */
  mobile?: "title" | "field" | "hidden";
}

interface DataTableProps<T> {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  loading?: boolean;
  error?: { message: string; correlationId?: string } | null;
  onRetry?: () => void;
  sort?: SortState | null;
  onSortChange?: (sort: SortState) => void;
  onRowClick?: (row: T) => void;
  /** Пустое состояние различается: «пусто» и «не найдено по фильтру» —
   *  разные сообщения, требование §15.4. */
  empty?: { title: string; description?: string; action?: ReactNode };
  caption?: string;
  /** Итоговая строка под таблицей — теми же столбцами, без сортировки. */
  footer?: T;
}

export function DataTable<T>(props: DataTableProps<T>) {
  const phone = useIsPhone();
  return phone ? <DataCards {...props} /> : <DataTableView {...props} />;
}

function DataTableView<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  error,
  onRetry,
  sort,
  onSortChange,
  onRowClick,
  empty,
  caption,
  footer,
}: DataTableProps<T>) {
  const blank = tableState({ rows, loading, error, onRetry, empty });
  if (blank !== null) return blank;

  const handleSort = (column: Column<T>) => {
    if (column.sortable !== true || !onSortChange) return;
    const direction: SortDirection =
      sort?.field === column.key && sort.direction === "asc" ? "desc" : "asc";
    onSortChange({ field: column.key, direction });
  };

  return (
    // Широкая таблица скроллится внутри своего контейнера: страница
    // не должна ехать горизонтально целиком.
    <div className="w-full overflow-x-auto">
      <table className="w-full border-collapse text-sm">
        {caption && <caption className="sr-only">{caption}</caption>}
        <thead>
          <tr className="border-b border-line-strong">
            {columns.map((column) => {
              const active = sort?.field === column.key;
              const SortIcon = !active ? ArrowUpDown : sort.direction === "asc" ? ArrowUp : ArrowDown;

              return (
                <th
                  key={column.key}
                  scope="col"
                  style={column.width === undefined ? undefined : { width: column.width }}
                  aria-sort={
                    active ? (sort.direction === "asc" ? "ascending" : "descending") : undefined
                  }
                  className={cn(
                    "px-3 py-2 text-[11px] font-bold tracking-wide text-faint uppercase",
                    column.align === "right" ? "text-right" : "text-left",
                  )}
                >
                  {column.sortable === true && onSortChange ? (
                    <button
                      type="button"
                      onClick={() => handleSort(column)}
                      className={cn(
                        "inline-flex items-center gap-1 hover:text-fg",
                        active && "text-fg",
                      )}
                    >
                      {column.header}
                      <SortIcon className="size-3" aria-hidden />
                    </button>
                  ) : (
                    column.header
                  )}
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={rowKey(row)}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              className={cn(
                "border-b border-line last:border-0",
                onRowClick && "cursor-pointer hover:bg-sunken",
              )}
            >
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cn(
                    "px-3 py-2.5 text-fg",
                    column.align === "right" ? "text-right" : "text-left",
                    column.numeric === true && "tabular-nums",
                  )}
                >
                  {column.render(row)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {footer !== undefined && (
          <tfoot>
            <tr className="border-t border-line-strong bg-sunken font-semibold">
              {columns.map((column) => (
                <td
                  key={column.key}
                  className={cn(
                    "px-3 py-2.5 text-fg",
                    column.align === "right" ? "text-right" : "text-left",
                    column.numeric === true && "tabular-nums",
                  )}
                >
                  {column.render(footer)}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

function DataCards<T>({
  columns,
  rows,
  rowKey,
  loading = false,
  error,
  onRetry,
  sort,
  onSortChange,
  onRowClick,
  empty,
  caption,
  footer,
}: DataTableProps<T>) {
  const blank = tableState({ rows, loading, error, onRetry, empty });
  if (blank !== null) return blank;

  const visible = columns.filter((column) => column.mobile !== "hidden");
  const marked = visible.filter((column) => column.mobile === "title");
  const titles = marked.length > 0 ? marked : visible.slice(0, 1);
  const fields = visible.filter((column) => !titles.includes(column));
  const sortable = onSortChange ? columns.filter((column) => column.sortable === true) : [];

  return (
    <div className="flex flex-col gap-2">
      {sortable.length > 0 && onSortChange && (
        <div className="flex items-center gap-2">
          <div className="min-w-0 flex-1">
            <Select
              label="Сортировка"
              placeholder="Сортировка"
              value={sort?.field ?? null}
              options={sortable.map((column) => ({ id: column.key, label: column.header }))}
              onChange={(field) => onSortChange({ field, direction: sort?.direction ?? "asc" })}
            />
          </div>
          {sort && (
            <IconButton
              icon={sort.direction === "asc" ? ArrowUp : ArrowDown}
              label={sort.direction === "asc" ? "По возрастанию" : "По убыванию"}
              variant="secondary"
              onClick={() =>
                onSortChange({
                  field: sort.field,
                  direction: sort.direction === "asc" ? "desc" : "asc",
                })
              }
            />
          )}
        </div>
      )}

      <ul className="flex flex-col gap-2" aria-label={caption}>
        {[...rows, ...(footer === undefined ? [] : [footer])].map((row, index) => (
          <li
            key={index === rows.length ? "__footer" : rowKey(row)}
            onClick={onRowClick && index < rows.length ? () => onRowClick(row) : undefined}
            className={cn(
              "rounded-lg border border-line p-3 text-sm",
              index === rows.length ? "bg-sunken font-semibold" : "bg-surface",
              onRowClick && index < rows.length && "cursor-pointer active:bg-sunken",
            )}
          >
            <div className="flex flex-wrap items-baseline gap-x-2 text-fg">
              {titles.map((column) => (
                <div key={column.key} className="min-w-0">
                  {column.render(row)}
                </div>
              ))}
            </div>
            {fields.length > 0 && (
              <dl className="mt-2 grid grid-cols-[minmax(0,2fr)_minmax(0,3fr)] gap-x-3 gap-y-1.5">
                {fields.map((column) =>
                  // Столбец без подписи — действия строки: во всю ширину.
                  column.header === "" ? (
                    <dd key={column.key} className="col-span-2 flex justify-end">
                      {column.render(row)}
                    </dd>
                  ) : (
                    <div key={column.key} className="contents">
                      <dt className="text-[12px] text-faint">{column.header}</dt>
                      <dd
                        className={cn(
                          "min-w-0 text-fg",
                          column.numeric === true && "tabular-nums",
                        )}
                      >
                        {column.render(row)}
                      </dd>
                    </div>
                  ),
                )}
              </dl>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Загрузка, ошибка и пустой список — одни для таблицы и карточек. */
function tableState<T>({
  rows,
  loading,
  error,
  onRetry,
  empty,
}: {
  rows: T[];
  loading: boolean;
  error: DataTableProps<T>["error"] | undefined;
  onRetry: (() => void) | undefined;
  empty: DataTableProps<T>["empty"] | undefined;
}): ReactNode {
  if (error) {
    return (
      <ErrorState
        message={error.message}
        {...(error.correlationId === undefined ? {} : { correlationId: error.correlationId })}
        {...(onRetry === undefined ? {} : { onRetry })}
      />
    );
  }

  if (loading) return <SkeletonRows rows={6} className="p-4" />;

  if (rows.length === 0) {
    return (
      <EmptyState
        title={empty?.title ?? "Пока ничего нет"}
        {...(empty?.description === undefined ? {} : { description: empty.description })}
        {...(empty?.action === undefined ? {} : { action: empty.action })}
      />
    );
  }

  return null;
}
